import { useApp } from '@/app/AppContext'
import { formatPercent } from '@/lib/format'
import { Value } from './ReportShell'
import { firstNumber, seriesOf } from './types'
import type { LoadedWidget } from './types'

/**
 * Отрисовка виджета дашборда (ТЗ §4.6) — тикет #77.
 *
 * <h2>Почему без библиотеки графиков</h2>
 *
 * Новая зависимость ради этого тикета не заводилась. Число, таблица и воронка рисуются
 * разметкой и CSS, столбики и линия — инлайновым SVG: это десятки строк, они читаются, и
 * они не тянут в бандл сто килобайт с собственной темой, которую пришлось бы мирить с
 * тёмной темой приложения.
 *
 * Круговая диаграмма, спидометр и тепловая карта (#94) — тоже инлайновый SVG и разметка.
 * Где данные не несут форму честно — отрицательная доля у круга, нет второй величины для
 * шкалы спидометра, одно измерение у тепловой карты, — виджет говорит это словами и
 * показывает таблицу: подсунуть вместо круга столбики или выдумать шкалу значит соврать
 * зрителю о том, что он видит.
 *
 * <h2>Отказ рисуется отказом</h2>
 *
 * Виджет, чей источник закрыт смотрящему, приходит с `errorCode` и **без** строк.
 * Показать вместо него пустую таблицу значило бы сказать «данных нет» — то есть выдать
 * запрет за штатное состояние, и запрет остался бы невидимым.
 */

/** Виджет целиком: заголовок, источник и тело. */
export function WidgetCard({
  loaded,
  onRemove,
  actions,
  drag,
}: {
  loaded: LoadedWidget
  onRemove?: () => void
  /** Extra header controls: refresh for everyone, edit and move in edit mode (#94). */
  actions?: React.ReactNode
  /** Pointer drag in edit mode (#94); absent outside it. */
  drag?: {
    /** Header grab handle pressed. */
    onGrab: () => void
    /** The pointer is over this card while something is dragged. */
    onHover: () => void
    dragging: boolean
    target: boolean
  }
}) {
  const { t } = useApp()
  const { widget } = loaded

  return (
    <article
      className={`widget-card${drag?.dragging ? ' is-dragging' : ''}${drag?.target ? ' is-drop-target' : ''}`}
      data-widget-id={widget.id}
      onPointerEnter={drag ? drag.onHover : undefined}
      style={{
        gridColumn: `${widget.gridX + 1} / span ${widget.gridWidth}`,
        gridRow: `span ${widget.gridHeight}`,
      }}
    >
      <header className="widget-head">
        {drag && (
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-icon widget-grab"
            aria-label={t('dashboard.widget.drag', { title: widget.title })}
            onPointerDown={(event) => {
              event.preventDefault()
              drag.onGrab()
            }}
          >
            ⠿
          </button>
        )}
        <h3>{widget.title}</h3>
        <span className="muted widget-source">{widget.reportName ?? widget.dataSource}</span>
        {actions}
        {onRemove && (
          <button type="button" className="link" onClick={onRemove}>
            {t('dashboard.widget.remove')}
          </button>
        )}
      </header>
      <div className="widget-body">
        <WidgetBody loaded={loaded} />
      </div>
    </article>
  )
}

/** Тело виджета: отказ, пустота или картинка по типу. */
export function WidgetBody({ loaded }: { loaded: LoadedWidget }) {
  const { t } = useApp()
  const { widget, columns, rows, errorCode } = loaded

  // Отказ вперёд всего: он важнее типа виджета и не должен выглядеть пустой картинкой.
  if (errorCode) {
    const forbidden = errorCode === 'report.forbidden_source'
    return (
      <p role="alert" className="widget-error">
        {forbidden ? t('access.denied') : (loaded.errorMessage ?? t('app.error'))}
      </p>
    )
  }

  // `null` и пустой список различаются: первое сюда уже не дойдёт (это отказ), второе —
  // честное «за период ничего нет».
  if (!columns || !rows || rows.length === 0) {
    return <p className="muted">{t('dashboard.widget.empty')}</p>
  }

  switch (widget.widgetType) {
    case 'kpi':
      return <KpiWidget columns={columns} rows={rows} />
    case 'bar':
      return <BarWidget columns={columns} rows={rows} />
    case 'line':
      return <LineWidget columns={columns} rows={rows} />
    case 'funnel':
      return <FunnelWidget columns={columns} rows={rows} />
    case 'table':
      return <TableWidget columns={columns} rows={rows} />
    // #94: drawn with inline SVG too. Where the data cannot carry the shape honestly (a
    // negative share, no scale for the gauge, one dimension for the heat map) the widget
    // says so and falls back to the table — the shape is never faked.
    case 'pie':
      return <PieWidget columns={columns} rows={rows} />
    case 'gauge':
      return <GaugeWidget columns={columns} rows={rows} />
    case 'heatmap':
      return <HeatmapWidget columns={columns} rows={rows} />
  }
}

/** The table with a sentence saying why the chosen shape was not drawn. */
function Fallback({ reason, columns, rows }: Data & { reason: string }) {
  return (
    <>
      <p className="muted widget-fallback">{reason}</p>
      <TableWidget columns={columns} rows={rows} />
    </>
  )
}

/** Slices beyond this many are summed into «other»: a pie of thirty slivers reads as noise. */
const PIE_SLICES = 6

/**
 * Pie (#94): shares of the first numeric column by the first other column.
 *
 * Shares only exist for non-negative parts of a positive whole. A negative value (a refund,
 * a loss) has no slice: drawing its absolute value would show money coming in where it went
 * out, so such data falls back to the table and says why. Every slice carries its label,
 * value and share in the legend — colour alone carries nothing.
 */
function PieWidget({ columns, rows }: Data) {
  const { t, locale } = useApp()
  const series = seriesOf(columns, rows)
  if (series.length === 0) return <p className="muted">{t('dashboard.widget.empty')}</p>
  if (series.some((point) => point.value < 0)) {
    return <Fallback reason={t('dashboard.widget.pie.negative')} columns={columns} rows={rows} />
  }
  const total = series.reduce((sum, point) => sum + point.value, 0)
  if (total <= 0) return <p className="muted">{t('dashboard.widget.empty')}</p>

  const sorted = [...series].sort((a, b) => b.value - a.value)
  const head = sorted.slice(0, PIE_SLICES)
  const rest = sorted.slice(PIE_SLICES).reduce((sum, point) => sum + point.value, 0)
  const slices = rest > 0 ? [...head, { label: t('dashboard.widget.pie.other'), value: rest }] : head

  let angle = 0
  const percent = (value: number) =>
    formatPercent(value / total, 1, locale)

  return (
    <div className="widget-pie">
      <svg className="widget-pie-svg" viewBox="-1 -1 2 2" role="img" aria-label={t('dashboard.widget.type.pie')}>
        {slices.map((slice, index) => {
          const share = slice.value / total
          const start = angle
          angle += share * 2 * Math.PI
          return (
            <path
              key={`${slice.label}-${index}`}
              d={arcPath(start, angle)}
              className={`widget-slice widget-slice-${index % PIE_SLICES}`}
            >
              <title>{`${slice.label}: ${slice.value} (${percent(slice.value)})`}</title>
            </path>
          )
        })}
      </svg>
      <ul className="widget-legend">
        {slices.map((slice, index) => (
          <li key={`${slice.label}-${index}`}>
            <span className={`widget-swatch widget-slice-${index % PIE_SLICES}`} aria-hidden />
            <span className="widget-legend-label">{slice.label}</span>
            <span className="widget-legend-value">
              <Value value={slice.value} /> · {percent(slice.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** One sector of the unit circle, from 12 o'clock clockwise; a whole circle is two halves. */
export function arcPath(start: number, end: number): string {
  if (end - start >= 2 * Math.PI - 1e-9) {
    return 'M 0 -1 A 1 1 0 1 1 0 1 A 1 1 0 1 1 0 -1 Z'
  }
  const point = (a: number) => `${Math.sin(a).toFixed(5)} ${(-Math.cos(a)).toFixed(5)}`
  const large = end - start > Math.PI ? 1 : 0
  return `M 0 0 L ${point(start)} A 1 1 0 ${large} 1 ${point(end)} Z`
}

/**
 * Gauge (#94): the first number of the first row against the second number of that row.
 *
 * A gauge needs a scale, and the report has to bring it — «fact» next to «plan». Without a
 * second number there is no honest 100%: inventing one (the maximum of the column, a round
 * number) would draw a needle against a target nobody set, so the widget shows the number
 * and says the scale is missing. Above the scale the arc is full and the overshoot is said
 * in words rather than drawn past the end.
 */
function GaugeWidget({ columns, rows }: Data) {
  const { t, locale } = useApp()
  const first = rows[0] ?? {}
  const numeric = columns.filter((column) => {
    const raw = first[column]
    return raw !== null && raw !== undefined && raw !== '' && Number.isFinite(Number(raw))
  })
  const [valueColumn, scaleColumn] = numeric
  if (!valueColumn) return <p className="muted">{t('dashboard.widget.empty')}</p>
  const value = Number(first[valueColumn])
  const scale = scaleColumn ? Number(first[scaleColumn]) : null
  if (scale === null || scale <= 0) {
    return (
      <div className="widget-kpi">
        <strong className="widget-kpi-value">
          <Value value={value} />
        </strong>
        <span className="muted">{valueColumn}</span>
        <span className="muted widget-fallback">{t('dashboard.widget.gauge.noScale')}</span>
      </div>
    )
  }
  const ratio = value / scale
  const filled = Math.max(0, Math.min(1, ratio))
  // Half circle from 9 o'clock to 3 o'clock: angle runs from -π/2 to +π/2.
  const end = -Math.PI / 2 + filled * Math.PI
  const at = (a: number) => `${Math.sin(a).toFixed(5)} ${(-Math.cos(a)).toFixed(5)}`
  return (
    <div className="widget-gauge">
      <svg className="widget-gauge-svg" viewBox="-1.1 -1.1 2.2 1.25" role="img"
        aria-label={t('dashboard.widget.gauge.label', { value: String(value), scale: String(scale) })}>
        <path className="widget-gauge-track" d={`M ${at(-Math.PI / 2)} A 1 1 0 0 1 ${at(Math.PI / 2)}`} />
        {filled > 0 && (
          <path className="widget-gauge-fill" d={`M ${at(-Math.PI / 2)} A 1 1 0 0 1 ${at(end)}`} />
        )}
      </svg>
      <strong className="widget-kpi-value">
        {formatPercent(ratio, 0, locale)}
      </strong>
      <span className="muted">
        {valueColumn}: <Value value={value} /> / {scaleColumn}: <Value value={scale} />
      </span>
      {ratio > 1 && <span className="muted">{t('dashboard.widget.gauge.over')}</span>}
    </div>
  )
}

/** Cells drawn at most: a heat map of a thousand cells is a texture, not an answer. */
const HEAT_LIMIT = 20

/**
 * Heat map (#94): two label columns as rows and columns, the first numeric column as the
 * colour. Intensity is proportional to the value from zero, like the bars — starting from
 * the minimum would make a 2% difference look like black and white. Every cell also prints
 * its number: shade alone cannot be read back as a value. A missing pair is an empty cell,
 * not a zero: «nothing was recorded» and «zero was recorded» are different findings.
 */
function HeatmapWidget({ columns, rows }: Data) {
  const { t } = useApp()
  const isNumeric = (column: string) =>
    rows.some((row) => row[column] !== null && row[column] !== '' && Number.isFinite(Number(row[column])))
  const valueColumn = columns.find(isNumeric)
  const labels = columns.filter((column) => column !== valueColumn && !isNumeric(column))
  const [rowColumn, colColumn] = labels
  if (!valueColumn || !rowColumn || !colColumn) {
    return <Fallback reason={t('dashboard.widget.heatmap.needsTwo')} columns={columns} rows={rows} />
  }
  const rowKeys = unique(rows.map((row) => String(row[rowColumn] ?? '—'))).slice(0, HEAT_LIMIT)
  const colKeys = unique(rows.map((row) => String(row[colColumn] ?? '—'))).slice(0, HEAT_LIMIT)
  const cells = new Map<string, number>()
  for (const row of rows) {
    const value = Number(row[valueColumn])
    if (!Number.isFinite(value)) continue
    const key = `${String(row[rowColumn] ?? '—')}\u001f${String(row[colColumn] ?? '—')}`
    cells.set(key, (cells.get(key) ?? 0) + value)
  }
  const max = Math.max(...[...cells.values()].map(Math.abs), 1)
  return (
    <div className="widget-table-wrap">
      <table className="widget-heatmap" aria-label={t('dashboard.widget.type.heatmap')}>
        <thead>
          <tr>
            <th scope="col">{`${rowColumn} \\ ${colColumn}`}</th>
            {colKeys.map((key) => <th key={key} scope="col">{key}</th>)}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((rowKey) => (
            <tr key={rowKey}>
              <th scope="row">{rowKey}</th>
              {colKeys.map((colKey) => {
                const value = cells.get(`${rowKey}\u001f${colKey}`)
                return (
                  <td key={colKey} className="widget-heat-cell"
                    style={value === undefined ? undefined : { ['--heat' as string]: (Math.abs(value) / max).toFixed(3) }}>
                    {value === undefined ? '' : <Value value={value} />}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function unique(values: string[]): string[] {
  return [...new Set(values)]
}

type Data = { columns: string[]; rows: Record<string, unknown>[] }

/**
 * Число: первая числовая величина первой строки.
 *
 * Показывает и имя колонки, которую взял: догадка о том, «какая тут главная цифра»,
 * обязана быть видимой. Молчаливый выбор колонки означал бы, что зритель смотрит на
 * величину, названия которой не знает.
 */
function KpiWidget({ columns, rows }: Data) {
  const { t } = useApp()
  const { value, column } = firstNumber(columns, rows)

  if (value === null) return <p className="muted">{t('dashboard.widget.empty')}</p>
  return (
    <div className="widget-kpi">
      <strong className="widget-kpi-value">
        <Value value={value} />
      </strong>
      <span className="muted">{column}</span>
    </div>
  )
}

/** Столбики: инлайновый SVG, без зависимостей. */
function BarWidget({ columns, rows }: Data) {
  const { t } = useApp()
  const series = seriesOf(columns, rows).slice(0, 12)
  if (series.length === 0) return <p className="muted">{t('dashboard.widget.empty')}</p>

  // Масштаб от нуля, а не от минимума: столбики, начатые от минимального значения,
  // преувеличивают разницу в разы — это классический способ соврать картинкой.
  const max = Math.max(...series.map((point) => Math.abs(point.value)), 1)
  const width = 100 / series.length

  return (
    <svg className="widget-chart" viewBox="0 0 100 60" preserveAspectRatio="none" role="img">
      {series.map((point, index) => {
        const height = (Math.abs(point.value) / max) * 52
        return (
          <rect
            key={`${point.label}-${index}`}
            x={index * width + width * 0.15}
            y={56 - height}
            width={width * 0.7}
            height={height}
            className="widget-bar"
          >
            <title>{`${point.label}: ${point.value}`}</title>
          </rect>
        )
      })}
    </svg>
  )
}

/** Линия: тот же SVG, точки соединены. */
function LineWidget({ columns, rows }: Data) {
  const { t } = useApp()
  const series = seriesOf(columns, rows).slice(0, 60)
  if (series.length < 2) {
    // Линия из одной точки — это не линия: показываем числом, а не отрезком в никуда.
    return <KpiWidget columns={columns} rows={rows} />
  }

  const max = Math.max(...series.map((point) => point.value), 0)
  const min = Math.min(...series.map((point) => point.value), 0)
  const span = max - min || 1
  const step = 100 / (series.length - 1)

  const points = series
    .map((point, index) => `${index * step},${56 - ((point.value - min) / span) * 52}`)
    .join(' ')

  return (
    <svg className="widget-chart" viewBox="0 0 100 60" preserveAspectRatio="none" role="img">
      <polyline className="widget-line" points={points} fill="none" />
      <title>{t('dashboard.widget.line')}</title>
    </svg>
  )
}

/**
 * Воронка: полосы убывающей ширины.
 *
 * Ширина считается от **первой** ступени, а не от максимальной: воронка это доля от
 * входа, и нормировка по максимуму сделала бы самую широкую ступень стопроцентной, даже
 * если до неё дошла половина.
 */
function FunnelWidget({ columns, rows }: Data) {
  const { t } = useApp()
  const series = seriesOf(columns, rows)
  const head = series[0]
  if (!head) return <p className="muted">{t('dashboard.widget.empty')}</p>

  const top = Math.abs(head.value) || 1

  return (
    <ol className="widget-funnel">
      {series.map((point, index) => {
        const share = Math.max(0, Math.min(100, (Math.abs(point.value) / top) * 100))
        return (
          <li key={`${point.label}-${index}`}>
            <span className="widget-funnel-label">{point.label}</span>
            <span className="widget-funnel-bar" style={{ width: `${share}%` }} />
            <span className="widget-funnel-value">
              <Value value={point.value} />
            </span>
          </li>
        )
      })}
    </ol>
  )
}

/** Таблица: колонки в том порядке, в котором их вернул отчёт. */
function TableWidget({ columns, rows }: Data) {
  return (
    <div className="widget-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((row, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td key={column}>
                  <Value value={row[column] as string | number | null} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
